import api, { Paginated } from '.'
import { IFollow } from './interfaces/IFollow'
import { IActivity } from './interfaces/IActivity'
import {
  IAttendanceInformationR,
  IUser,
  IUserChannelNewsRO,
  IUserInfoRO,
  IUserRO,
} from './interfaces/IUser'
import { UiTheme } from './types/user'
import { ISave } from './interfaces/ISave'
import { IBaseUser } from './interfaces/IBaseUser'
import { IElection } from './interfaces/IElection'
import { IPost } from './interfaces/IPost'
import { ILanguage } from './interfaces/ILanguage'
import { NationDB } from './interfaces/INations'
import { IBasePagination } from './interfaces/IBasePagination'
import { IImage } from './interfaces/IImage'
import { IVideo } from './interfaces/IVideo'

export const UserApi = (accessToken?: string) => ({
  user: null,
  paginateUsers: async (params: IUser.IPaginate) => {
    const res = await api.get<Paginated<IUserRO>>(`/users`, {
      params,
    })

    return res.data
  },

  getProfile: async () => {
    if (typeof window === 'undefined' && !accessToken) {
      return undefined
    }

    const res = await api.get<MyProfile>(`/users/profile`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return res.data
  },

  getUserByUsername: async (username: string) => {
    const res = await api.get<IUserRO>(`/users/profile/${username}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return res.data
  },

  updateUser: async (body: IUser.IUpdate) => {
    const res = await api.patch(`/users`, body)

    return res.data
  },

  validateNickname: async (nickname: string) => {
    const res = await api.get<ValidateNameStatus>(
      `/users/validate/nickname/${nickname}`,
    )

    return res.data
  },

  validateUsername: async (username: string) => {
    const res = await api.get<ValidateNameStatus>(
      `/users/validate/username/${username}`,
    )

    return res.data
  },
  getUserInfo: async (username: string) => {
    const res = await api.get<IUserInfoRO>(`/users/${username}/info`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return res.data
  },
  getUserGroup: async (id: number) => {
    const res = await api.get(`/users/${id}/group`)

    return res.data
  },
  getGroupMembers: async (username: string) => {
    const res = await api.get<IBaseUser[]>(`/users/${username}/members`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return res.data
  },
  getUserProfileVote: async (username: string, query: IElection.IPaginate) => {
    const res = await api.get<IElection.IPaginateRO>(
      `/users/profile/vote/${username}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: query,
      },
    )

    return res.data
  },
  follow: async (username: string) => {
    return await api.post(`/follow/${username}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  },
  getFollowStatus: async (username: string) => {
    if (!accessToken) {
      return false
    }

    const res = await api.get<boolean>(`/follow/${username}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return res.data
  },
  getFollowing: async (username: string, params: IFollow.IPaginate) => {
    const res = await api.get<Paginated<IFollow.IFollowRO>>(
      `/follow/${username}/following`,
      {
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )

    return res.data
  },
  getFollower: async (username: string, params: IFollow.IPaginate) => {
    const res = await api.get<Paginated<IFollow.IFollowRO>>(
      `/follow/${username}/follower`,
      {
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )

    return res.data
  },
  addLover: async (id: number) => {
    const res = await api.post(`/lover/${id}`)

    return res.data
  },
  removeLover: async (id: number) => {
    const res = await api.delete(`/lover/${id}`)

    return res.data
  },
  paginateActivityFanboard: async (
    username: string,
    params: IBasePagination,
  ) => {
    const res = await api.get<IPost.IPaginateRO>(
      `/users/${username}/profile/activity/community`,
      { params, headers: { Authorization: `Bearer ${accessToken}` } },
    )

    return res.data
  },
  paginateActivityComment: async (
    username: string,
    params: IBasePagination,
  ) => {
    const res = await api.get<
      IActivity.ICommentPaginateRO<IActivity.ICommentRO>
    >(`/users/${username}/profile/activity/comment`, {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return res.data
  },
  paginateActivitySchedule: async (
    username: string,
    params: IBasePagination,
  ) => {
    const res = await api.get<IActivity.IPaginateRO<IActivity.IScheduleRO>>(
      `/users/${username}/profile/activity/schedule`,
      { params },
    )

    return res.data
  },

  paginateSaveFanboard: async (username: string, params: IBasePagination) => {
    const res = await api.get<IPost.IPaginateRO>(
      `/users/${username}/profile/save/community`,
      { params, headers: { Authorization: `Bearer ${accessToken}` } },
    )

    return res.data
  },
  paginateSaveSchedule: async (username: string, params: IBasePagination) => {
    const res = await api.get<ISave.IPaginateRO<ISave.IScheduleRO>>(
      `/users/${username}/profile/save/schedule`,
      { params, headers: { Authorization: `Bearer ${accessToken}` } },
    )

    return res.data
  },
  paginateSaveContent: async (username: string, params: IBasePagination) => {
    const res = await api.get<IVideo.IPaginateRO>(
      `/users/${username}/profile/save/content`,
      { params, headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return res.data
  },

  paginateSaveNews: async (username: string, params: IBasePagination) => {
    const res = await api.get<ISave.IPaginateRO<ISave.INewsRO>>(
      `/users/${username}/profile/save/news`,
      { params },
    )

    return res.data
  },
  attend: async (advertiseId?: string) => {
    const res = await api.post<{ dayCount: number }>('/users/attend', {
      advertiseId,
    })

    return res.data
  },

  getAttend: async (advertiseId?: string) => {
    const res = await api.get<{ dayCount: number }>('/users/attend', {
      params: {
        advertiseId,
      },
    })

    return res.data
  },

  getAttendSheet: async (advertiseId?: string) => {
    const res = await api.get<IAttendanceInformationR>(
      '/users/attendance-information',
      {
        params: {
          advertiseId,
        },
      },
    )

    return res.data
  },

  getBonusCoin: async (advertiseId: string) => {
    const res = await api.post<number>('/users/periodic-heart', {
      advertiseId,
    })

    return res.data
  },

  depart: async () => {
    const res = await api.delete<number>(`/users`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return res.data
  },

  updateProfileImage: async (
    username: string,
    body: IUser.IUpdateProfileImage,
  ) => {
    const res = await api.patch<boolean>(
      `/users/${username}/profile-image`,
      body,
    )

    return res.data
  },

  reorderLover: async (usernames: string[]) => {
    const res = await api.patch('/users/lovers/order', {
      usernames,
    })

    return res.data
  },
})

export type MyProfile = {
  id: number
  hash: string
  username: string
  nickname: string
  introduction: string
  followerCount: number
  followingCount: number
  currentDiamondCoin: number
  currentHeartCoin: number
  enableActivityPush: boolean
  enableEventPush: boolean
  enableFollowingArtistPush: boolean
  enablePush: boolean
  hiddenProfile: boolean
  language: ILanguage
  nation: NationDB
  showActivity: boolean
  showFollowing: boolean
  showSave: boolean
  showVote: boolean
  uiTheme: UiTheme
  profileImage: IImage | null
}

export enum ValidateNameStatus {
  SUCCESS = 'SUCCESS',
  INVALID_LENGTH = 'INVALID_LENGTH',
  BLANK_EXIST = 'BLANK_EXIST',
  EMOJI_EXIST = 'EMOJI_EXIST',
  DUPLICATE = 'DUPLICATE',
  NOT_ENGLISH_OR_NUMBER = 'NOT_ENGLISH_OR_NUMBER',
}
